import type { LocationDescriptorObject } from 'history'
import type { Lang, Strings, TFunction } from 'language/translator'
import type { TAnalysisDto, TAnalysisTypeDto } from '@homepal/api-contract'

import {
  AnalysisGeneration,
  AnalysisGenerationStatus
} from 'types/AnalysisGeneration'
import { TAnalysisType } from 'redux/hooks/AnalysisTypes'
import { TDashboardLocationState } from 'types/GlobalDashboardFilter'

// Set by the backend when the user cancelled the run.
const CANCELLED_STATUS_CODE = 499

const RUN_ERROR_KEYS: Record<number, Strings<'analysis_types'>> = {
  403: 'run_error_403',
  422: 'run_error_422',
  502: 'run_error_502',
  503: 'run_error_503',
  504: 'run_error_504'
}

/**
 * What the customer is told about a finished run, keyed on its status
 * code. Only an analysis type's own abort reason is shown verbatim.
 */
export const describeGenerationOutcome = (
  t: TFunction,
  generation: AnalysisGeneration
): string | null => {
  if (generation.status === AnalysisGenerationStatus.SUCCEEDED) {
    return null
  }

  if (generation.status_code === CANCELLED_STATUS_CODE) {
    return t('analysis_types', 'run_cancelled')
  }

  if (generation.status === AnalysisGenerationStatus.ABORTED) {
    return generation.message ?? t('analysis_types', 'run_aborted_body')
  }

  const key = generation.status_code
    ? RUN_ERROR_KEYS[generation.status_code]
    : undefined

  return t('analysis_types', key ?? 'run_error_default')
}

/** A failed run's technical line — for Homepal users only. */
export const generationErrorDetail = (
  generation: AnalysisGeneration
): string | null =>
  generation.status === AnalysisGenerationStatus.FAILED
    ? generation.message
    : null

/** Whether the closed accounting period is required but not set. */
export const periodIsUnmet = (analysisType: TAnalysisTypeDto): boolean => {
  const period = analysisType.data_requirements?.closed_accounting_period

  return period ? period.required && !period.set : false
}

/** A generation timestamp as "14 aug 06:11". */
export const formatGenerationTime = (iso: string, lang: Lang): string =>
  new Date(iso).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })

/** The analysis types followed up on the results page: the activated ones. */
export const getFollowedAnalysisTypes = (
  analysisTypes: TAnalysisType[]
): TAnalysisType[] => analysisTypes.filter((item) => item.active)

export const isMissingConfiguration = (
  analysisType: TAnalysisTypeDto
): boolean => analysisType.status === 'MISSING_CONFIGURATION'

/**
 * Only version 2 analysis types, and their analyses, can leave a property
 * out.
 */
export const canExclude = (item: { version: number }): boolean =>
  item.version === 2

/**
 * Only an activated analysis type with its configuration in place can run.
 */
export const canRun = (analysisType: TAnalysisTypeDto): boolean =>
  analysisType.status === 'ACTIVE'

/**
 * Opens the investigation dashboard, filtered on the analysis' selected
 * properties when there is a version 2 analysis with a selection.
 */
export const getInvestigationDashboardLocation = (
  dashboardId: string,
  analysis: TAnalysisDto | null
): LocationDescriptorObject<TDashboardLocationState> => {
  const attribute = analysis?.metric_filter_attribute_id ?? null
  const selected =
    analysis && analysis.version === 2
      ? analysis.properties.filter((property) => property.selected)
      : []

  return {
    pathname: `/dashboards/${dashboardId}`,
    state:
      attribute && selected.length > 0
        ? {
            dashboardFilter: selected.map((property) => ({
              attribute_option_id: null,
              attribute,
              value: property.name
            }))
          }
        : undefined
  }
}
